import {
    ActionIcon,
    Group,
    Popover,
    Stack,
    Text,
    Tooltip,
    VisuallyHidden,
} from "@mantine/core";
import {useState} from "react";
import {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import IconClipboard from "../../assets/clipboard.svg?react";
import IconX from "../../assets/x.svg?react";
import {PasteChipData} from "../../features/chat/utils/pasteAttachments";
import classes from "./PasteChip.module.css";

interface PasteChipProps extends PasteChipData {
    onRemove?: (id: string) => void;
    /**
     * The body opens a preview of the pasted text on hover. Only the chips in
     * the thread use this option. A pending paste in the composer does not use
     * it.
     */
    preview?: boolean;
}

const pasteName = (t: TFunction) =>
    t("chat.paste_attachment.name", {defaultValue: "Pasted text"});

const linesLabel = (t: TFunction, lineCount: number) =>
    t("chat.paste_attachment.chip", {
        defaultValue: "{{lines}} lines",
        lines: lineCount,
    });

/** A screen reader reads the name, the number, and the size. The chip includes the number only if it has one. */
const chipAnnouncement = (t: TFunction, {lineCount, number}: PasteChipData) =>
    [
        number === undefined ? pasteName(t) : `${pasteName(t)} #${number}`,
        linesLabel(t, lineCount),
    ].join(", ");

export function PasteChip({
    id,
    lineCount,
    truncated,
    content,
    preview = false,
    onRemove,
}: PasteChipProps) {
    const {t} = useTranslation();
    const canPreview = preview && !!content;
    const [opened, setOpened] = useState(false);

    const chip = (
        <span
            className={`${classes.chip} ${canPreview ? classes.chipInteractive : ""}`}
            data-name="paste-chip"
            role={canPreview ? "button" : undefined}
            tabIndex={canPreview ? 0 : undefined}
            aria-expanded={canPreview ? opened : undefined}
            onClick={canPreview ? () => setOpened((o) => !o) : undefined}
            onKeyDown={
                canPreview
                    ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setOpened((o) => !o);
                          }
                      }
                    : undefined
            }
        >
            <span className={classes.icon} aria-hidden="true">
                <IconClipboard />
            </span>
            <Stack gap={2} className={classes.body}>
                <Text className={classes.title}>{pasteName(t)}</Text>
                <Group gap="xs" wrap="nowrap">
                    <Text
                        c="gneis.8"
                        className={classes.meta}
                        data-name="paste-chip-lines"
                    >
                        {linesLabel(t, lineCount)}
                    </Text>
                    {truncated && (
                        <Tooltip
                            label={t("chat.paste_attachment.truncated_tooltip", {
                                defaultValue:
                                    "The paste was too long and was cut short before sending.",
                            })}
                            openDelay={200}
                            withArrow
                        >
                            <span className={classes.truncated}>
                                {t("chat.paste_attachment.truncated", {
                                    defaultValue: "truncated",
                                })}
                            </span>
                        </Tooltip>
                    )}
                </Group>
            </Stack>
            {onRemove && (
                <ActionIcon
                    className={classes.remove}
                    variant="transparent"
                    size="xs"
                    onClick={(event) => {
                        event.stopPropagation();
                        onRemove(id);
                    }}
                    aria-label={t("chat.paste_attachment.remove", {
                        defaultValue: "Remove attachment",
                    })}
                >
                    <IconX />
                </ActionIcon>
            )}
        </span>
    );

    if (!canPreview) return chip;

    return (
        <Popover
            opened={opened}
            onChange={setOpened}
            position="bottom-start"
            radius={0}
            shadow="md"
            width={420}
            middlewares={{flip: true, shift: true}}
            trapFocus
        >
            <Popover.Target>{chip}</Popover.Target>
            <Popover.Dropdown className={classes.previewCard} p={0}>
                <Group
                    justify="space-between"
                    gap="md"
                    wrap="nowrap"
                    className={classes.previewTitleBar}
                >
                    <Text className={classes.previewName}>{pasteName(t)}</Text>
                    <Text className={classes.previewLines}>
                        {linesLabel(t, lineCount)}
                    </Text>
                </Group>
                <div className={classes.previewScroll} tabIndex={-1}>
                    <pre className={classes.previewBody}>{content}</pre>
                </div>
            </Popover.Dropdown>
        </Popover>
    );
}

interface PasteChipListProps {
    chips: PasteChipData[];
    onRemove?: (id: string) => void;
    justify?: "flex-start" | "flex-end";
    /**
     * A screen reader reads the chips again when they change. The composer uses
     * this option. A screen reader then says that the paste became an
     * attachment.
     */
    announce?: boolean;
    /** Each chip previews its pasted text on hover. This applies only in the thread. */
    preview?: boolean;
}

export function PasteChipList({
    chips,
    onRemove,
    justify = "flex-start",
    announce = false,
    preview = false,
}: PasteChipListProps) {
    const {t} = useTranslation();

    if (chips.length === 0 && !announce) return null;
    return (
        <>
            {announce && (
                <VisuallyHidden role="status" aria-live="polite">
                    {chips.map((chip) => chipAnnouncement(t, chip)).join(", ")}
                </VisuallyHidden>
            )}
            {chips.length > 0 && (
                <Group gap="xs" justify={justify} className={classes.list}>
                    {chips.map((chip) => (
                        <PasteChip
                            key={chip.id}
                            {...chip}
                            preview={preview}
                            onRemove={onRemove}
                        />
                    ))}
                </Group>
            )}
        </>
    );
}
